import { PropsWithChildren, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, usePopoverMenu } from '@frontend/design-system';
import { PROVIDER_LANE_WIDTH } from '../../constants';
import { orNone } from '../../utils';

export type ColumnHeaderContentWrapperProps = {
  shouldSetMaxWidth: boolean;
  isClickable: boolean;
  isInReorderMode: boolean;
  columnHeaderMenu: (popoverMenuProps: ReturnType<typeof usePopoverMenu>) => React.ReactNode;
  id: string;
  isOverlay?: boolean;
};

export const ColumnHeaderContentWrapper = ({
  shouldSetMaxWidth,
  isClickable,
  isInReorderMode,
  children,
  columnHeaderMenu,
  id,
  isOverlay = false,
}: PropsWithChildren<ColumnHeaderContentWrapperProps>) => {
  const popoverMenuProps = usePopoverMenu({ placement: 'bottom-end' });
  const { getTriggerProps, isOpen: isMenuOpen } = popoverMenuProps;
  const menuButtonContainer = useRef<HTMLDivElement>(null);

  const onClickCapture = (e: React.MouseEvent) => {
    // Prevent drag start when clicking on the menu button
    const eventOccurredInButtonContainer =
      (e.target !== menuButtonContainer.current &&
        menuButtonContainer.current &&
        menuButtonContainer.current.contains(e.target as Node)) ||
      e.target === popoverMenuProps.refs.floating.current ||
      popoverMenuProps.refs.floating.current?.contains(e.target as Node);
    if (isInReorderMode && !eventOccurredInButtonContainer) {
      e.stopPropagation();
    }
  };

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  return (
    <div
      {...attributes}
      ref={setNodeRef}
      css={[
        styles.containerStyles(shouldSetMaxWidth, isClickable),
        styles.containerHoverStyles({ isOverlay, isMenuOpen, isInReorderMode }),
      ]}
      style={styles.draggable({ isOverlay, isDragging, transform, transition })}
      onClickCapture={onClickCapture}
    >
      <div className='column-entity-drag-handle' css={styles.iconContainer} {...listeners}>
        <Icon name='dragand-drop-handle' size={20} color='subdued' />
      </div>
      <div className='column-entity-info-container' css={styles.entityInfo} {...(isInReorderMode ? listeners : {})}>
        {children}
      </div>
      <div css={styles.buttonContainer} className='column-header-button-container' ref={menuButtonContainer}>
        <Button iconName='more' variant='secondary' {...getTriggerProps()} data-dnd-ignore-input='true' />
        {typeof columnHeaderMenu === 'function' && columnHeaderMenu(popoverMenuProps)}
      </div>
    </div>
  );
};

const styles = {
  draggable: ({
    isDragging,
    isOverlay,
    transform,
    transition,
  }: {
    isDragging: boolean;
    isOverlay: boolean;
    transform: any;
    transition: any;
  }) => ({
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging && { opacity: 0.2 }),
    ...(isOverlay && {
      transform: `rotate(-10deg)`,
      boxShadow: theme.shadows.floating,
      borderRadius: theme.borderRadius.medium,
      backgroundColor: theme.colors.white,
      padding: theme.spacing(0.5),
      zIndex: theme.zIndex.highest,
      height: 'auto',
      minWidth: '0px',
      width: 'fit-content',
    }),
  }),
  entityInfo: css`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    flex: 1;
    transition: padding 0.2s ease-in-out;
  `,
  buttonContainer: css`
    position: absolute;
    right: ${theme.spacing(0.5)};
    top: 50%;
    transform: translateY(-50%);
    display: none;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;

    > button {
      padding: ${theme.spacing(0.25)};
      height: auto;
    }
  `,
  iconContainer: css`
    position: absolute;
    left: ${theme.spacing(0.5)};
    top: 50%;
    transform: translateY(-50%);
    display: none;
    opacity: 0;
    flex-shrink: 0;
    cursor: grab;
    transition: opacity 0.2s ease-in-out;
  `,
  containerStyles: (shouldSetMaxWidth: boolean, isClickable: boolean) =>
    css({
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      minWidth: PROVIDER_LANE_WIDTH,
      maxWidth: shouldSetMaxWidth ? PROVIDER_LANE_WIDTH : 'unset',
      padding: theme.spacing(0, 1),
      '> div': { cursor: isClickable ? 'pointer' : 'default' },
    }),
  containerHoverStyles: ({
    isOverlay,
    isMenuOpen,
    isInReorderMode,
  }: {
    isOverlay: boolean;
    isMenuOpen: boolean;
    isInReorderMode: boolean;
  }) =>
    // 1. when dragging, remove max-width to prevent text truncation
    // 2. make drag controls visible on hover or if in drag mode
    // 3. hide menu button when dragging to prevent interference with drag action
    css`
      ${orNone(isOverlay && `&`)} {
        max-width: none;
        cursor: grab;
        border-left: 4px solid ${theme.colors.primary50};
      }
      ${orNone((isInReorderMode && `&`) || `&:hover`)} {
        > div.column-entity-drag-handle {
          display: flex;
          opacity: 1;
          cursor: grab;
        }
        > div.column-entity-info-container {
          padding: ${theme.spacing(0, 2.5)};
        }
      }
      ${orNone(isInReorderMode && `&`)} {
        > div {
          cursor: grab;
        }
      }
      ${orNone((isMenuOpen && `&`) || `${!isOverlay && `&:hover`}`)} {
        > .column-header-button-container {
          display: flex;
          opacity: 1;
        }
        > div.column-entity-info-container {
          padding: ${theme.spacing(0, 2.5)};
        }
      }
    `,
};
